import type {
  MockSectionCustomerInput,
  ReportCustomerDto,
  SectionId,
} from '@act/shared/data-transfer-objects';
import {
  SECTION_IDS,
  isMockHeroKpiSnapshot,
  mockSectionData,
  buildHeroTagline,
  normalizeConnectionsBehavioralCareSnapshot,
  normalizeConnectionsPrimaryCareSnapshot,
  normalizeBhMedicalDiagnosesSnapshot,
  normalizeClinicalCareOutcomesSnapshot,
  normalizeClosingValueSnapshot,
  normalizeWhatsPossibleAtMaturitySnapshot,
  normalizeLookingAheadSnapshot,
  normalizeVbpEngagementSnapshot,
  normalizeWhatMembersNeedSnapshot,
  normalizeWhoWereReachingSnapshot,
  buildHarpEnrollmentSnapshotFromRows,
  buildGapClosuresSnapshotFromRows,
  buildHomeVisitsSnapshotFromRows,
} from '@act/shared/data-transfer-objects';

/**
 * Returns whether a snapshot object has at least one key.
 * @param dataSnapshot - Stored section snapshot JSON.
 * @returns True when the snapshot is non-empty.
 */
export const hasSectionSnapshotData = (
  dataSnapshot: Record<string, unknown> | null | undefined,
): boolean => {
  return Boolean(dataSnapshot && Object.keys(dataSnapshot).length > 0);
};

/**
 * Maps API customer DTO to mock snapshot generator input.
 * @param customer - Report customer from the reports API.
 * @returns Input shape for {@link mockSectionData}.
 */
export const toMockSectionCustomerInput = (
  customer: ReportCustomerDto,
): MockSectionCustomerInput => ({
  id: customer.id,
  name: customer.name,
  usStateCode: customer.usStateCode ?? null,
});

const isSectionId = (sectionId: string): sectionId is SectionId => {
  return (SECTION_IDS as readonly string[]).includes(sectionId);
};

export interface ResolveSectionSnapshotParams {
  sectionId: string;
  dataSnapshot: Record<string, unknown> | null | undefined;
  customer: ReportCustomerDto | null | undefined;
  reportingPeriod: string | null | undefined;
  measurementPeriod?: string | null | undefined;
}

/**
 * Resolves the snapshot JSON used for section preview: stored data when present,
 * otherwise deterministic mock data for draft mode.
 * @param params - Section id, optional stored snapshot, and report context.
 * @returns Snapshot object for the renderer, or null when context is still loading.
 */
export const resolveSectionSnapshot = ({
  sectionId,
  dataSnapshot,
  customer,
  reportingPeriod,
  measurementPeriod,
}: ResolveSectionSnapshotParams): Record<string, unknown> | null => {
  if (sectionId === 'hero') {
    const heroMeasurementPeriod =
      measurementPeriod ?? reportingPeriod ?? '';
    if (hasSectionSnapshotData(dataSnapshot) && dataSnapshot) {
      if (isMockHeroKpiSnapshot(dataSnapshot)) {
        return { ...dataSnapshot, kpiTiles: [] };
      }
      return dataSnapshot;
    }
    if (!customer || !reportingPeriod) {
      return null;
    }
    return {
      customerName: customer.name,
      reportingPeriod,
      payer: 'Other',
      headline:
        'Your hardest-to-reach members are getting the help they need',
      tagline: buildHeroTagline(customer.name, heroMeasurementPeriod),
      kpiTiles: [],
    };
  }

  if (sectionId === 'vbp-engagement') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeVbpEngagementSnapshot(base, customerInput, reportingPeriod);
  }

  if (sectionId === 'who-were-reaching') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeWhoWereReachingSnapshot(
      base,
      customerInput,
      reportingPeriod,
    );
  }

  if (sectionId === 'what-members-need') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeWhatMembersNeedSnapshot(
      base,
      customerInput,
      reportingPeriod,
    );
  }

  if (sectionId === 'connections-behavioral-care') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeConnectionsBehavioralCareSnapshot(
      base,
      customerInput,
      reportingPeriod,
    );
  }

  if (sectionId === 'connections-primary-care') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeConnectionsPrimaryCareSnapshot(
      base,
      customerInput,
      reportingPeriod,
    );
  }

  if (sectionId === 'bh-medical-diagnoses') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeBhMedicalDiagnosesSnapshot(
      base,
      customerInput,
      reportingPeriod,
    );
  }

  if (sectionId === 'clinical-care-outcomes') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeClinicalCareOutcomesSnapshot(
      base,
      customerInput,
      reportingPeriod,
    );
  }

  if (sectionId === 'closing-value') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeClosingValueSnapshot(base, customerInput, reportingPeriod);
  }

  if (sectionId === 'whats-possible-at-maturity') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeWhatsPossibleAtMaturitySnapshot(
      base,
      customerInput,
      reportingPeriod,
    );
  }

  if (sectionId === 'looking-ahead') {
    if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
      return null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    const base = hasSectionSnapshotData(dataSnapshot)
      ? (dataSnapshot as Record<string, unknown>)
      : mockSectionData(sectionId, customerInput, reportingPeriod);
    return normalizeLookingAheadSnapshot(base, customerInput, reportingPeriod);
  }

  if (
    sectionId === 'harp-enrollment' ||
    sectionId === 'gap-closures' ||
    sectionId === 'home-visits'
  ) {
    if (!customer || !reportingPeriod) {
      return hasSectionSnapshotData(dataSnapshot)
        ? (dataSnapshot as Record<string, unknown>)
        : null;
    }
    const customerInput = toMockSectionCustomerInput(customer);
    if (hasSectionSnapshotData(dataSnapshot) && dataSnapshot) {
      return dataSnapshot;
    }
    if (sectionId === 'harp-enrollment') {
      return buildHarpEnrollmentSnapshotFromRows(
        [],
        customerInput,
        reportingPeriod,
      );
    }
    if (sectionId === 'gap-closures') {
      return buildGapClosuresSnapshotFromRows(
        [],
        customerInput,
        reportingPeriod,
      );
    }
    return buildHomeVisitsSnapshotFromRows([], customerInput, reportingPeriod);
  }

  if (hasSectionSnapshotData(dataSnapshot)) {
    return dataSnapshot ?? null;
  }

  if (!customer || !reportingPeriod || !isSectionId(sectionId)) {
    return null;
  }

  return mockSectionData(
    sectionId,
    toMockSectionCustomerInput(customer),
    reportingPeriod,
  );
};
